<template>
    <div ref="root" class="single-select">
        <label v-if="label" class="single-select__label">{{ label }}</label>

        <button
            ref="trigger"
            type="button"
            class="single-select__trigger"
            :class="{ 'single-select__trigger--open': isOpen, 'single-select__trigger--placeholder': !modelValue }"
            :aria-expanded="isOpen"
            aria-haspopup="listbox"
            @click="toggle"
            @keydown.down.prevent="openAndFocus(0)"
            @keydown.up.prevent="openAndFocus(options.length - 1)"
            @keydown.esc="close"
        >
            <span class="single-select__value">{{ modelValue || placeholder }}</span>
            <span class="single-select__chevron" aria-hidden="true"></span>
        </button>

        <ul
            v-if="isOpen"
            ref="menu"
            class="single-select__menu"
            role="listbox"
            :aria-label="label || placeholder"
            @keydown.esc.prevent="closeAndFocusTrigger"
            @keydown.down.prevent="moveFocus(1)"
            @keydown.up.prevent="moveFocus(-1)"
            @keydown.home.prevent="focusOption(0)"
            @keydown.end.prevent="focusOption(options.length - 1)"
        >
            <li
                v-for="(option, index) in options"
                :key="option"
                class="single-select__option"
                :class="{ 'single-select__option--selected': option === modelValue }"
                role="option"
                :aria-selected="option === modelValue"
                tabindex="-1"
                @click="select(option)"
                @keydown.enter.prevent="select(option)"
                @keydown.space.prevent="select(option)"
            >
                {{ option }}
            </li>
        </ul>
    </div>
</template>

<script>
export default {
    name: "SingleSelect",

    props: {
        label: {
            type: String,
            default: "",
        },
        options: {
            type: Array,
            default: () => [],
            validator: (options) => options.every((option) => typeof option === "string"),
        },
        placeholder: {
            type: String,
            default: "Select an option",
        },
        modelValue: {
            type: String,
            default: "",
        },
    },

    emits: ["update:modelValue"],

    data() {
        return {
            isOpen: false,
        };
    },

    mounted() {
        document.addEventListener("pointerdown", this.handleOutsideClick);
    },

    beforeUnmount() {
        document.removeEventListener("pointerdown", this.handleOutsideClick);
    },

    methods: {
        toggle() {
            this.isOpen = !this.isOpen;
        },

        close() {
            this.isOpen = false;
        },

        closeAndFocusTrigger() {
            this.close();
            this.$nextTick(() => this.$refs.trigger.focus());
        },

        select(option) {
            this.$emit("update:modelValue", option);
            this.closeAndFocusTrigger();
        },

        handleOutsideClick(event) {
            if (!this.$refs.root?.contains(event.target)) {
                this.close();
            }
        },

        openAndFocus(index) {
            this.isOpen = true;
            this.$nextTick(() => this.focusOption(index));
        },

        focusOption(index) {
            const options = this.$refs.menu?.querySelectorAll('[role="option"]');
            options?.[Math.max(0, Math.min(index, options.length - 1))]?.focus();
        },

        moveFocus(direction) {
            const options = [...this.$refs.menu.querySelectorAll('[role="option"]')];
            const currentIndex = options.indexOf(document.activeElement);
            this.focusOption(currentIndex + direction);
        },
    },
};
</script>

<style scoped>
.single-select {
    position: relative;
    width: 100%;
    max-width: 100%;
    color: #080808;
    font-family: Arial, Helvetica, sans-serif;
}

.single-select__label {
    display: block;
    margin: 0 0 4px 2px;
    color: #5f5f5f;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1px;
}

.single-select__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 40px;
    padding: 0 12px 0 12px;
    border: 1px solid #a8a8a8;
    border-radius: 8px;
    background: #ffffff;
    color: #080808;
    cursor: pointer;
    font: inherit;
    font-size: 20px;
    line-height: 1;
    text-align: left;
}

.single-select__value {
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    margin-right: 8px;
}

.single-select__trigger:focus,
.single-select__trigger--open {
    outline: 2px solid #ff4e00;
    outline-offset: 1.5px;
}

.single-select__trigger--placeholder {
    color: #989898;
}

.single-select__chevron {
    width: 0;
    height: 0;
    border-top: 6px solid #858585;
    border-right: 6px solid transparent;
    border-left: 6px solid transparent;
}

.single-select__menu {
    position: absolute;
    z-index: 10;
    top: calc(100% + 3px);
    left: 3px;
    width: calc(100% - 6px);
    margin: 0;
    padding: 0;
    overflow: hidden;
    border: 1px solid #777777;
    background: #ffffff;
    list-style: none;
}

.single-select__option {
    padding: 6px 12px;
    cursor: pointer;
    font-size: 14px;
    line-height: 14px;
    outline: none;
}

.single-select__option:hover,
.single-select__option:focus-visible,
.single-select__option--selected {
    background: #858585;
    color: #ffffff;
}
</style>
